import { inject } from '@angular/core';

import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  InMemoryCache,
  InMemoryCacheConfig,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';

import { SessionStorageKeysEnum } from '@shared/lib/constants';
import { ObjectType } from '@shared/lib/types';

/**
 * Creates Apollo GraphQL client options for Angular's `provideApollo()`.
 *
 * This function sets up:
 * - HTTP transport to the GraphQL endpoint
 * - Authentication middleware with Bearer tokens and custom headers
 * - Error handling for GraphQL, protocol, and network errors
 * - In-memory cache with default type policies
 *
 * @param params Configuration object for initializing the Apollo client.
 *
 * @param params.httpLinkOptions Options for the HTTP transport link.
 *   Typically includes `uri` (GraphQL endpoint) and fetch options.
 *
 * @param params.options Optional additional settings.
 * @param params.options.customHeaders {ObjectType} Optional headers to include
 *   in every GraphQL request (e.g., subscription keys, tenant metadata).
 *
 * @param params.inMemoryCacheOptions Optional configuration for the Apollo
 *   `InMemoryCache`. Can override or extend default cache behavior and type policies.
 *
 * @param params.apolloClientOptions Optional additional Apollo client options
 *   merged into the final `ApolloClient.Options`.
 *
 * @returns {ApolloClient.Options} Configured Apollo client options ready
 *   for use with `provideApollo()`.
 *
 * @example
 * provideApollo(() =>
 *   createApolloProvider({
 *     httpLinkOptions: { uri: environment.graphqlEndpoint },
 *     options: { customHeaders: { 'Ocp-Apim-Subscription-Key': environment.subscriptionKey } }
 *   })
 * );
 */
export function createApolloProvider({
  httpLinkOptions,
  options = {},
  inMemoryCacheOptions = {},
  apolloClientOptions = {},
}: {
  httpLinkOptions: HttpLink.Options;
  options?: { customHeaders?: ObjectType };
  // operationContextOptions?: ;
  inMemoryCacheOptions?: InMemoryCacheConfig;
  apolloClientOptions?: Partial<ApolloClient.Options>;
}): ApolloClient.Options {
  // Create the HTTP link responsible for sending GraphQL requests
  const httpHandler = inject(HttpLink).create({ ...httpLinkOptions });

  /**
   * Apollo middleware link to attach authentication and custom headers
   * to each outgoing GraphQL request.
   */
  const apolloLink = new ApolloLink((operation, forward) => {
    // Modify request context before sending
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,

        // Add Bearer token for authorization
        Authorization: `Bearer ${sessionStorage.getItem(SessionStorageKeysEnum.ACCESS_TOKEN)}`, // Bearer token

        // Custom authentication type from current user (or default)
        AuthenticationType: sessionStorage.getItem(
          SessionStorageKeysEnum.AUTH_TYPE,
        ),
        // Standard content type for GraphQL
        'Content-Type': 'application/json',

        // Enable tracing in API management layers
        'Ocp-Apim-Trace': 'true',

        // Merge any optional custom headers from config
        ...(options?.customHeaders || {}),
      },
    }));

    // Forward the operation to the next link in the chain
    return forward(operation);
  });

  /**
   * Apollo error link to handle GraphQL, protocol, and network errors.
   *
   * Currently logs errors to the console; can be extended to integrate
   * with monitoring/logging services.
   */
  const errorLink = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      // GraphQL errors returned by server
      error.errors.forEach(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    } else if (CombinedProtocolErrors.is(error)) {
      // Protocol-level errors (e.g., auth, schema issues)
      error.errors.forEach(({ message, extensions }) =>
        console.log(
          `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
            extensions,
          )}`,
        ),
      );
    } else {
      // Network or other unhandled errors
      console.error(`[${operation.operationName}] Network error:`, error);
    }
  });

  // Return the fully configured Apollo client options
  return {
    // Combine links: error handling -> auth headers -> HTTP transport
    link: ApolloLink.from([errorLink, apolloLink, httpHandler]),

    // Configure normalized in-memory cache with type policies
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            /**
             * Normalize the `config` query field to a cached `Config` reference
             */
            config: {
              read: (_, { toReference }) =>
                toReference({ __typename: 'Config' }),
            },

            /**
             * Normalize the `search` query field to a cached `Search` reference
             */
            search: {
              read: (_, { toReference }) =>
                toReference({ __typename: 'Search' }),
            },
          },
        },
      },
      // Merge optional cache configuration
      ...inMemoryCacheOptions,
    }),

    // Merge optional Apollo client options
    ...apolloClientOptions,
  };
}
